import { ConfirmDialog, PageHeader, Surface } from '@clickguard/design-system'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useFeatureFlag, useFeatureFlagsStatus } from 'services/feature-flags'
import { useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { PageTitle } from 'utils/page-title'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { ProtectionSettingsCard } from 'components/protection-settings/protection-settings-card'
import {
	protectionToSettings,
	settingsToProtection
} from 'components/protection-settings/protection-adapters'
import { useSignalCoverage } from 'components/protection-settings/signal-coverage'
import { ProtectionSettings } from 'components/protection-settings/protection-values'
import { OwnSettingsList } from './components/own-settings-list'
import 'components/page-surface/page-surface.css'

const DETECTION_MODE_LABELS: Record<number, string> = {
	1: 'quiet',
	2: 'softer',
	3: 'normal',
	4: 'stricter',
	5: 'aggressive'
}

/**
 * Protection › AI Protection (DEV-2422 §1).
 *
 * Ships with Custom Rules behind the SAME flag — two flags would be a way to release a
 * half-migrated nav, and the ticket asks for one cutover.
 */
export const AiProtectionPage = () => {
	const {
		asset,
		adAccounts,
		demoMode,
		setAccountProtection,
		refreshAdAccounts
	} = useAssetStore(
		useShallow((state) => ({
			asset: state.asset,
			adAccounts: state.adAccounts,
			demoMode: state.demoMode,
			setAccountProtection: state.setAccountProtection,
			refreshAdAccounts: state.refreshAdAccounts
		}))
	)
	const trackPostHogEvent = useTrackPostHogEvent()
	// Masks plan-gated signals out of the payload; the card already renders them off.
	const covers = useSignalCoverage()
	// Resolves the pending confirm below. Held as a function IN a function, because
	// `setState` would otherwise treat the resolver as an updater and call it.
	const [confirmCascade, setConfirmCascade] = useState<
		((confirmed: boolean) => void) | null
	>(null)
	const cascadeRetryAssetId = useRef<string | null>(null)
	// The exact set `deactivateAllForAsset` will walk: the store's list is already
	// scoped to this website (`asset-store.ts:433`), same as the server's query.
	const activeAdAccounts = adAccounts.filter(
		(adAccount) => adAccount.status === 'ACTIVE'
	).length

	// The route is reachable by URL, so the flag has to gate the PAGE, not just the
	// sidebar row. `useFeatureFlag` returns false while PostHog is still answering as
	// well as when the flag is off, so `flagsReady` separates "not allowed" from "not
	// answered yet".
	const aiProtectionEnabled = useFeatureFlag('custom_rules_v2')
	const { flagsReady } = useFeatureFlagsStatus()

	const accountId = demoMode ? 'workspace-demo' : asset?.account_id
	const assetId = demoMode ? 'website-demo' : asset?.id

	// Read ONCE per asset: the card owns its draft from mount, so re-seeding it on every
	// store write would throw away whatever the user is in the middle of typing.
	const savedSettings = useMemo(
		() => protectionToSettings(asset?.protection),
		[asset?.protection]
	)

	// Divider appears only once the header is stuck — PageLayout's sentinel recipe.
	const sentinelRef = useRef<HTMLDivElement>(null)
	const [isStuck, setIsStuck] = useState(false)

	useEffect(() => {
		const sentinel = sentinelRef.current
		if (!sentinel || typeof IntersectionObserver === 'undefined') return
		const observer = new IntersectionObserver(
			([entry]) => setIsStuck(!entry.isIntersecting),
			{ threshold: 0 }
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [])

	const handleSave = async (next: ProtectionSettings | null) => {
		if (!next || !accountId || !assetId) return
		// Monitoring off and the box unticked is the churn switch (§1.1): the server turns
		// click detection off for every ad account under this website, so their spend stops
		// counting toward the plan limit. ONE-WAY — turning monitoring back on restores
		// nothing, and nothing records which accounts were switched off.
		//
		// Gated on the TRANSITION, never on the state, and this is the whole reason:
		// `keepTracking` is not persisted (`protection-adapters.ts:67`), so a website
		// already saved off WITH the box ticked — its ad accounts deliberately left
		// ACTIVE and tracking — reopens with the box unticked. Off-to-off would then
		// deactivate every one of them on a save that changed nothing. The ad-account and
		// campaign drawers already guard their own cascades this way (`acc.status ===
		// 'ACTIVE'`, `!ignored.includes`); the website level is the one that did not.
		const turningOff = !next.aiEnabled && !!asset?.protection?.enabled
		const retryingCascade = cascadeRetryAssetId.current === assetId
		const disableClickDetection =
			!next.aiEnabled && !next.keepTracking && (turningOff || retryingCascade)
		// Irreversible and bulk, so it asks first. Cancelling THROWS rather than returning:
		// the card clears `dirty` and flips to "Saved" on any resolved `onSave`, and only
		// treats a rejection as "nothing was saved" (`protection-settings-card.tsx:810`).
		if (disableClickDetection && !retryingCascade) {
			const confirmed = await new Promise<boolean>((resolve) =>
				setConfirmCascade(() => resolve)
			)
			setConfirmCascade(null)
			if (!confirmed) throw new Error('Click-detection cascade cancelled')
			cascadeRetryAssetId.current = assetId
		}
		// `asset.protection` carries fields with no UI (`vpn`) that the PATCH would
		// otherwise blank — it REPLACES the stored object rather than merging.
		await setAccountProtection(accountId, assetId, {
			...settingsToProtection(next, asset?.protection, { covers }),
			...(disableClickDetection ? { disable_click_detection: true } : {})
		})
		cascadeRetryAssetId.current = null
		// The cascade changes ad-account status, which the page's own panel and the
		// Account Overview rollup both read.
		if (disableClickDetection) await refreshAdAccounts()
		trackPostHogEvent(
			PostHogEvent.SAVE_AI_PROTECTION_SETTINGS,
			PostHogPath.AI_PROTECTION,
			{
				status: next.aiEnabled ? 'enabled' : 'disabled',
				fraud_detection: next.sensitivityMode === 'custom' ? 'Custom' : 'AI',
				custom_tuning:
					next.sensitivityMode === 'custom'
						? DETECTION_MODE_LABELS[next.aggressiveness]
						: null,
				protective_actions:
					next.protectionLevel === 'monitoring' ? 'monitoring' : 'block'
			}
		)
	}

	// Below every hook on purpose: returning earlier would change the hook order.
	if (!flagsReady) return null
	if (!aiProtectionEnabled) return <Navigate to=".." replace />

	return (
		<>
			<PageTitle pageName="AI Protection" />
			<Surface
				bg="canvas"
				radius="none"
				bordered={false}
				className="cg-page-surface min-h-full"
			>
				{/* One tall: the distance the page travels before the header is stuck. The
				    negative margin cancels the height. */}
				<div ref={sentinelRef} aria-hidden="true" className="-mb-1 h-1" />
				<div
					className={`cg-page-surface cg-header-bleed sticky -top-1 z-20 border-b transition-colors ${
						isStuck ? 'border-[rgb(var(--cg-border))]' : 'border-transparent'
					}`}
				>
					<div className="px-6 pb-3 pt-4">
						<PageHeader
							layout="inline"
							title="AI Protection"
							subtitle="Fine-tune AI-driven protection settings"
						/>
					</div>
				</div>

				<div className="flex flex-col gap-4 p-6 pt-4">
					<ProtectionSettingsCard
						// Remount on a new asset rather than re-seed: `initialValues` is read
						// once, which is also what drops an unsaved draft when the user
						// switches website.
						key={assetId}
						scope="website"
						initialValues={savedSettings}
						parentValues={savedSettings}
						asidePanel={<OwnSettingsList />}
						onSave={handleSave}
					/>
				</div>
			</Surface>
			{/* `closeOnConfirm={false}`: the save runs after this resolves, and the card
			    owns the spinner — closing on confirm is what the resolver does. */}
			<ConfirmDialog
				open={confirmCascade !== null}
				tone="danger"
				title="Stop detecting clicks on this website?"
				description={
					activeAdAccounts > 0
						? `Turning monitoring off without keeping click detection will deactivate ${activeAdAccounts} ad ${
								activeAdAccounts === 1 ? 'account' : 'accounts'
						  } under this website. This cannot be undone — turning monitoring back on does not reactivate them.`
						: 'Turning monitoring off without keeping click detection deactivates every ad account under this website. This cannot be undone — turning monitoring back on does not reactivate them.'
				}
				confirmLabel="Stop detecting clicks"
				closeOnConfirm={false}
				onConfirm={() => confirmCascade?.(true)}
				onClose={() => confirmCascade?.(false)}
			/>
		</>
	)
}
